import { type Prettify } from '@shared/utils';
import {
  useResendPasswordResetEmailMutation,
  type ResendPasswordResetEmailMutationOptions,
} from '@user-management/graphql';
import { useCallback } from 'react';

type ResendUserInvitationEmailOptions = Prettify<
  Pick<
    ResendPasswordResetEmailMutationOptions,
    'onCompleted' | 'onError' | 'refetchQueries' | 'awaitRefetchQueries'
  > & { userId: string }
>;

/**
 * Hook to send a user invitation email and update the user's invitation status in the cache.
 *
 * @param options - Configuration options for sending the invitation email
 * @param options.userId - The unique identifier of the user to invite
 * @param options.onCompleted - Optional callback function triggered when the email is sent successfully
 * @param options.onError - Optional callback function triggered if there's an error sending the email
 *
 * @returns An object containing the resendUserInvitationEmail function to trigger the invitation email
 */
export function useResendUserInvitationEmail({
  userId,
  onCompleted,
  onError,
  refetchQueries,
  awaitRefetchQueries,
}: ResendUserInvitationEmailOptions) {
  const [resendPasswordResetEmail, { loading: isResendingPasswordResetEmail }] =
    useResendPasswordResetEmailMutation({
      onCompleted: (data) => {
        if (!data?.suResendResetPasswordEmail?.success) {
          throw new Error('Failed to resend invitation email');
        }
        onCompleted?.(data);
      },
      onError,
      refetchQueries,
      awaitRefetchQueries,
    });

  const resendUserInvitationEmail = useCallback(async () => {
    await resendPasswordResetEmail({
      variables: {
        userId,
      },
    });
  }, [resendPasswordResetEmail, userId]);

  return {
    resendUserInvitationEmail,
    isResendingPasswordResetEmail,
  };
}
